import { BREAKPOINT_VIEWPORTS } from '@/features/page-editor/lib/responsive'

/**
 * How wide a previewed Cogo application has to be LAID OUT before its own
 * markup makes sense.
 *
 * ── WHY THIS IS NOT A FLOOR ───────────────────────────────────────────────
 *
 * The card used to pin `minWidth: BREAKPOINT_VIEWPORTS.mobile.width` and stop
 * there, on the reasoning that 375px is the narrowest layout the Studio ever
 * reviews at. Rendering a real corpus dashboard through the card and reading
 * the DOM back is what disproved it. The grid the renderer emits for a
 * four-stat row is
 *
 *   class="grid gap-6 grid-cols-4 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1"
 *
 * — a BASE of four columns with min-width overrides layered on. Below 640px
 * no prefix matches and the base four columns apply; from 1024px `lg:` puts
 * four columns back. The row is four columns at every width the chat rail
 * could ever have, so a floor does not decide whether it reflows; it only
 * decides how little room each of the four gets. At 375px that is 93px per
 * stat, which is the two-line "Total Orders" with its neighbours clipped.
 *
 * The prefixes are answered by CSS @media, i.e. by the WINDOW: nothing on the
 * chat-card path mounts `BreakpointProvider`, so `useContainerBreakpoint()` is
 * `Infinity` and `resolveLayoutClasses` deliberately emits `sm:`/`md:`/`lg:`
 * for the stylesheet rather than resolving them against a container.
 *
 * So the rule this module states is a REQUIREMENT, read off the application:
 * the box must be at least as wide as the widest breakpoint the markup itself
 * depends on. Then whichever layout the markup selects has the room that
 * layout was written for. An application that names no breakpoint asks for
 * nothing and keeps the narrowest viewport the Studio reviews at.
 */

/**
 * Tailwind's own min-width breakpoints — what the prefixes MEAN.
 *
 * Mirrors `TAILWIND_BP` in `@cogo/react-renderer`'s container-renderer, which
 * is the code that emits the prefixes this module reads. The mirror is not
 * trusted: the gate collects prefixes from the RENDERED tree, so a renderer
 * that starts emitting a breakpoint this table does not know reddens there.
 */
export const TAILWIND_MIN_WIDTH_PX: Record<string, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

const RESPONSIVE_PREFIX_RE = /(?:^|\s)(sm|md|lg|xl|2xl):/g

/**
 * The viewports the Studio already renders and screenshot-reviews an
 * application at, narrowest first. Taken from the one map rather than listed
 * again here, so the preview cannot open at a width the project does not
 * otherwise claim to support.
 */
const REVIEWED_WIDTHS_PX: readonly number[] = Object.values(BREAKPOINT_VIEWPORTS)
  .map((viewport) => viewport.width)
  .sort((a, b) => a - b)

/**
 * Every Tailwind breakpoint this application's markup depends on.
 *
 * Two shapes, because the renderer emits prefixes from both: a `className`
 * that spells the prefix itself, and a `layout.responsive` / `responsive` map
 * whose KEYS are breakpoint names (`resolveLayoutClasses` turns
 * `responsive: { lg: { columns: 4 } }` into `lg:grid-cols-4`). Only those two
 * positions are read — a bare object key named `md` anywhere else is not a
 * breakpoint and must not widen anything.
 */
export function declaredBreakpoints(node: unknown): string[] {
  const found = new Set<string>()

  const visit = (value: unknown, underResponsive: boolean): void => {
    if (Array.isArray(value)) {
      for (const entry of value) visit(entry, false)
      return
    }
    if (!value || typeof value !== 'object') return
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (underResponsive && key in TAILWIND_MIN_WIDTH_PX) found.add(key)
      if (
        (key === 'className' || key === 'class') &&
        typeof child === 'string'
      ) {
        for (const match of ` ${child}`.matchAll(RESPONSIVE_PREFIX_RE)) {
          found.add(match[1])
        }
        continue
      }
      visit(child, key === 'responsive')
    }
  }

  visit(node, false)
  return [...found]
}

const BARE_GRID_COLUMNS_RE = /(?:^|\s)grid-cols-(\d+)/g

/**
 * Grid column counts this application asks for WITHOUT naming a breakpoint.
 *
 * Measured: of the 48 corpus blocks that declare a grid of two or more
 * columns, 23 declare no responsive behaviour anywhere — they say
 * `grid-cols-4`, or `layout: { columns: 4 }`, and mean it at every width. A
 * rule that read only breakpoint names would leave every one of those at the
 * 375px floor, which is the defect this module exists to remove.
 *
 * Column counts that appear INSIDE a `responsive` map are deliberately not
 * collected here: that map names its own breakpoint, and
 * `declaredBreakpoints` has already read it.
 */
export function declaredGridColumns(node: unknown): number[] {
  const found = new Set<number>()

  const visit = (value: unknown, underResponsive: boolean): void => {
    if (Array.isArray(value)) {
      for (const entry of value) visit(entry, false)
      return
    }
    if (!value || typeof value !== 'object') return
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (
        (key === 'className' || key === 'class') &&
        typeof child === 'string'
      ) {
        if (!underResponsive) {
          for (const match of ` ${child}`.matchAll(BARE_GRID_COLUMNS_RE)) {
            found.add(Number(match[1]))
          }
        }
        continue
      }
      if (key === 'columns' && typeof child === 'number' && !underResponsive) {
        found.add(child)
        continue
      }
      visit(child, underResponsive || key === 'responsive')
    }
  }

  visit(node, false)
  return [...found]
}

/**
 * The breakpoint at which an N-column grid is turned ON — as the corpus says
 * it, not as this file guesses it.
 *
 * A screen that says `grid-cols-4` with no prefix still needs the room four
 * columns need, and nothing in Tailwind states what that is. What DOES state
 * it is the body of blocks this project has already rendered and reviewed:
 * across `packages/cogo-schema/blocks-v2`, a two-column grid is turned on at
 * `sm` (32 declarations, against 9 at `md` and 13 at `lg`) and three, four and
 * five columns at `lg` (28/11/3, against 7/2/0 at `sm`).
 *
 * `preview-layout-width.corpus.test.ts` RE-DERIVES this table from those files
 * and fails when the corpus and this table disagree — including when the
 * corpus starts declaring a column count that is missing here. The table is a
 * cache of a measurement, never a decision taken in this file.
 */
const COLUMN_COUNT_BREAKPOINT: Record<number, string> = {
  2: 'sm',
  3: 'lg',
  4: 'lg',
  5: 'lg',
}

/** The widest band the table knows, for column counts beyond its rows. */
const WIDEST_TABLED_BREAKPOINT = Object.values(COLUMN_COUNT_BREAKPOINT).reduce(
  (widest, breakpoint) =>
    (TAILWIND_MIN_WIDTH_PX[breakpoint] ?? 0) > (TAILWIND_MIN_WIDTH_PX[widest] ?? 0)
      ? breakpoint
      : widest,
)

/** What one unprefixed N-column grid requires, in px. One column requires nothing. */
export function columnCountRequirementPx(columns: number): number {
  if (columns < 2) return 0
  const breakpoint =
    COLUMN_COUNT_BREAKPOINT[columns] ??
    // More columns than the corpus has ever declared needs at least as much
    // room as the most it has. Never less, which is the direction that hurts.
    WIDEST_TABLED_BREAKPOINT
  return TAILWIND_MIN_WIDTH_PX[breakpoint] ?? 0
}

/**
 * The width to lay this application out at.
 *
 * The single site that turns "what does this markup need" into a number. It
 * picks the NARROWEST reviewed viewport that still satisfies the requirement —
 * narrowest, so the preview is never wider than it has to be; still
 * satisfying, so the layout the markup selects is never squeezed. A
 * requirement past every reviewed viewport falls back to the requirement
 * itself rather than to the largest viewport, because the largest viewport
 * would be too narrow for it.
 */
export function requiredPreviewLayoutWidthPx(cogoJson: unknown): number {
  const required = Math.max(
    0,
    ...declaredBreakpoints(cogoJson).map(
      (breakpoint) => TAILWIND_MIN_WIDTH_PX[breakpoint] ?? 0,
    ),
    ...declaredGridColumns(cogoJson).map(columnCountRequirementPx),
  )
  if (required === 0) return BREAKPOINT_VIEWPORTS.mobile.width
  return (
    REVIEWED_WIDTHS_PX.find((width) => width >= required) ??
    Math.max(required, BREAKPOINT_VIEWPORTS.mobile.width)
  )
}
